// src/modules/app-views/leads/pages/Init/modals/LeadsParametersModal/modals/MetaConnectModal/Step1.jsx
// *******************************************************************************
// Step 1: Meta OAuth authorization
// Opens a popup to Meta, waits for the code, exchanges it for a long-lived token,
// then fetches the list of managed Facebook Pages.
// Stores results as hidden form values (_userAccessToken, _pages) so Step2 can use them.
// *******************************************************************************
import { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, Alert, Flex, Button } from 'antd';
import { FacebookFilled, CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';

// *******************************************************************************
// STEP CONTENT
// *******************************************************************************
const Step1Content = () => {
  const { form } = useSimpleFormContext();

  // 4. LOCAL STATE
  const [oauthState, setOauthState] = useState('idle'); // 'idle'|'loading_url'|'waiting'|'exchanging'|'done'|'error'
  const [errorMessage, setErrorMessage] = useState(null);
  const [pagesCount, setPagesCount] = useState(0);

  // 2. HOOKS & REFS
  const popupRef = useRef(null);
  const messageHandlerRef = useRef(null);

  // 5. EFFECTS — cleanup on unmount
  useEffect(() => {
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // 6. HANDLERS
  const handleAuthorize = useCallback(async () => {
    setOauthState('loading_url');
    setErrorMessage(null);

    try {
      const redirectUri = `${window.location.origin}/auth/meta-callback`;

      // Get OAuth URL from backend
      const [result] = await axiosService.post({
        url: API.DATA_SOURCES.META_AUTH_URL,
        data: { redirectUri },
      });

      const authUrl = result?.authUrl;
      if (!authUrl) throw new Error('No auth URL received from server');

      setOauthState('waiting');

      // Define popup message handler
      const onMessage = async event => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'META_OAUTH_CALLBACK') return;

        // Cleanup listener and close popup
        window.removeEventListener('message', onMessage);
        messageHandlerRef.current = null;
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }

        const { code, error, errorDescription } = event.data;

        if (error) {
          setOauthState('error');
          setErrorMessage(errorDescription || error);
          return;
        }

        if (!code) {
          setOauthState('error');
          setErrorMessage(translateX('meta_auth_no_code'));
          return;
        }

        setOauthState('exchanging');

        try {
          // Step 1: exchange code for long-lived user access token
          const [tokenResult] = await axiosService.post({
            url: API.DATA_SOURCES.META_EXCHANGE_TOKEN,
            data: { code, redirectUri },
          });

          const userAccessToken = tokenResult?.accessToken;
          if (!userAccessToken) throw new Error('No access token in server response');

          // Step 2: fetch list of managed Facebook Pages
          const [pagesResult] = await axiosService.post({
            url: API.DATA_SOURCES.META_PAGES,
            data: { userAccessToken },
          });

          const pages = pagesResult?.pages || [];

          // Store in form so Step2 can read them
          form.setFieldValue('_userAccessToken', userAccessToken);
          form.setFieldValue('_pages', pages);

          setPagesCount(pages.length);
          setOauthState('done');
        } catch (err) {
          console.error('[MetaConnectModal.Step1] token/pages error:', err);
          setOauthState('error');
          setErrorMessage(err?.response?.data?.message || err.message);
        }
      };

      messageHandlerRef.current = onMessage;
      window.addEventListener('message', onMessage);

      // Open Meta OAuth popup
      const popup = window.open(
        authUrl,
        'meta-oauth-popup',
        'width=620,height=720,left=200,top=80,scrollbars=yes',
      );
      popupRef.current = popup;

      // Detect if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        window.removeEventListener('message', onMessage);
        messageHandlerRef.current = null;
        setOauthState('error');
        setErrorMessage(translateX('meta_popup_blocked'));
      }
    } catch (err) {
      console.error('[MetaConnectModal.Step1] auth URL error:', err);
      setOauthState('error');
      setErrorMessage(err?.response?.data?.message || err.message);
    }
  }, [form]);

  // 8. UI
  const isLoading = oauthState === 'loading_url' || oauthState === 'exchanging';
  const isWaiting = oauthState === 'waiting';
  const isDone = oauthState === 'done';
  const isError = oauthState === 'error';

  return (
    <>
      {/* Instructions */}
      <Alert
        title={translateX('meta_connect_instructions_title')}
        description={translateX('meta_connect_instructions')}
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
      />

      {/* Authorize button */}
      <Flex justify="center" style={{ marginBottom: 20 }}>
        <Button
          type="primary"
          size="large"
          icon={isDone ? <CheckCircleFilled /> : <FacebookFilled />}
          loading={isLoading || isWaiting}
          onClick={handleAuthorize}
          disabled={isDone || isLoading || isWaiting}
          style={
            isDone
              ? { background: '#52c41a', borderColor: '#52c41a' }
              : { background: '#1877F2', borderColor: '#1877F2' }
          }
        >
          {isDone
            ? translateX('authorized')
            : isWaiting
              ? translateX('waiting_meta_auth')
              : isLoading
                ? translateX('exchanging_meta_token')
                : translateX('authorize_with_meta')}
        </Button>
      </Flex>

      {/* Status messages */}
      {isWaiting && (
        <Alert
          title={translateX('waiting_meta_auth')}
          description={translateX('meta_popup_instructions')}
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {isDone && (
        <Alert
          title={translateX('meta_auth_success')}
          description={`${pagesCount} ${translateX('pages_found')}`}
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {isError && (
        <Alert
          title={translateX('meta_auth_error')}
          description={errorMessage}
          type="error"
          showIcon
          icon={<WarningFilled />}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={handleAuthorize}>
              {translateX('retry')}
            </Button>
          }
        />
      )}

      {/* Hidden form fields to pass data to Step2.
          The required rule on _userAccessToken blocks "Next" until OAuth completes. */}
      <Form.Item
        name="_userAccessToken"
        hidden
        rules={[{ required: true, message: () => '' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="_pages" hidden>
        <Input />
      </Form.Item>
    </>
  );
};

// *******************************************************************************
// STEP BUILDER
// *******************************************************************************
const step1 = () => ({
  stepTitle: 'authorize_with_meta',
  content: () => <Step1Content />,
  // Block "Next" until authorization is complete
  validationFields: ['_userAccessToken'],
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default step1;
